import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceLine,
  Legend,
} from "recharts";
import { motion } from "framer-motion";
import { Info, ChevronLeft, ChevronRight, Download, RefreshCw, Calendar, LogOut } from "lucide-react";
import { loadTradingData, getAvailableDates, type CacheDay } from "@/lib/dataLoader";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";


/**
 * Human Trading Decision Collection — Initial Web App
 * - Single-file React prototype
 * - Loads per-day cached JSON (paste/upload)
 * - Sequential decision flow: d1 fundamentals → d2 market → d3 news → d4 sentiment → final
 * - Collects: action, reliability (1-100), rationale, timestamps, step
 * - Tracks portfolio across days

 */

// ---------- Types ----------

type StepKey = "d0" | "d1" | "d2" | "d3" | "d4" | "final";

type DecisionAction = "BUY" | "SELL" | "HOLD";

type DecisionRecord = {
  id: string;
  userId: string;
  sessionId: string;
  date: string; // YYYY-MM-DD (trading day)
  ticker: string;
  step: StepKey;
  action: DecisionAction;
  reliability: number; // 1..100
  rationale: string;
  createdAt: string; // ISO
  dataLeakage?: "yes" | "no"; // Only for d1-d4, not final
  sourceWeights?: { fundamentals: number; market: number; news: number; sentiment: number };
  timeSpent?: { fundamentals: number; market: number; news: number; sentiment: number }; // in seconds
  mostInfluential?: "price trend" | "market indices trend" | "fundamentals" | "market" | "news" | "sentiment";
  mostReliable?: "price trend" | "market indices trend" | "fundamentals" | "market" | "news" | "sentiment";
  tradePercentage?: number; // Only for final step (25, 50, 75, or 100)
};

// CacheDay type is imported from dataLoader

type UserProfile = {
  educationLevel: string;
  tradingExperience: string;
  isBusinessStudent: string;
  createdAt: string;
  lastLoginAt: string;
};

type PortfolioState = {
  cash: number;
  shares: number;
  lastPrice: number;
  history: { 
    date: string; 
    value: number; 
    pnl: number; 
    action?: DecisionAction; 
    cash: number; 
    shares: number; 
    lastPrice: number 
    reliability: number;
  }[];
};

// ---------- Helpers ----------

const STEPS: { key: StepKey; label: string; tab: StepKey }[] = [
  { key: "d0", label: "d0 Price", tab: "d0" },
  { key: "d1", label: "d1 Fundamentals", tab: "d1" },
  { key: "d2", label: "d2 Market", tab: "d2" },
  { key: "d3", label: "d3 News", tab: "d3" },
  { key: "d4", label: "d4 Sentiment", tab: "d4" },
  { key: "final", label: "Final", tab: "final" },
];

function uid(prefix = "rec") {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

function fmtMoney(x: number) {
  return x.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function pct(x: number) {
  return `${(x * 100).toFixed(2)}%`;
}

function storageGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function storageSet(key: string, value: any) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

// ---------- Mock Data (replace with your cache JSON) ----------

const MOCK_DAY: CacheDay = {
  date: "2024-01-02",
  ticker: "NVDA",
  fundamentals: {
    companyName: "NVIDIA Corporation",
    marketCap: "2.8T",
    pe: 28.1,
    eps: 6.13,
    revenueTTM: "$383B",
    grossMargin: 0.44,
    notes: ["Recent earnings beat consensus", "Strong services growth"],
  },
  market: {
    lastClose: 185.64,
    open: 184.1,
    high: 186.3,
    low: 183.6,
    volume: 56233411,
  },
  news: {
    items: [
      {
        title: "Apple supplier signals steady demand into Q1",
        source: "ExampleWire",
        time: "2024-01-02 09:15",
        summary: "Suppliers report stable orders; analysts cautious on hardware but optimistic on services.",
      },
      {
        title: "Regulatory scrutiny intensifies for app store policies",
        source: "MarketDaily",
        time: "2024-01-02 11:40",
        summary: "New inquiries may raise compliance costs; impact unclear.",
      },
    ],
  },
  sentiment: {
    score: 0.18,
    analystRating: "Moderate Buy",
    social: [
      { platform: "X", score: 0.12, sample: 1200 },
      { platform: "Reddit", score: 0.22, sample: 430 },
    ],
  },
  price30d: Array.from({ length: 30 }).map((_, i) => {
    const day = i - 29; // -29..0
    const base = 175;
    const price = base + (i * 0.35) + Math.sin(i / 3) * 1.2;
    const d = new Date("2024-01-02");
    d.setDate(d.getDate() + day);
    const iso = d.toISOString().slice(0, 10);
    return { date: iso, price: Math.round(price * 100) / 100, volume: 40_000_000 + i * 120_000 };
  }),
};

// ---------- UI Components ----------

function KeyValue({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="text-sm text-muted-foreground">{k}</div>
      <div className="text-sm font-medium text-right">{v}</div>
    </div>
  );
}

function SectionHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border bg-muted/30 p-3 text-sm text-muted-foreground">
      <Info className="mt-0.5 h-4 w-4" />
      <div>{children}</div>
    </div>
  );
}

// ---------- Main App ----------

export default function HumanTradingDecisionCollectionApp() {
  // Identity/session - ALWAYS start logged out to force login
  const [userId, setUserId] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionId] = useState(() => uid("sess"));
  const [loginInput, setLoginInput] = useState("");

  // User profile questions
  const [educationLevel, setEducationLevel] = useState<string>("");
  const [tradingExperience, setTradingExperience] = useState<string>("");
  const [isBusinessStudent, setIsBusinessStudent] = useState<string>("");
  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => storageGet("htdc_userProfile", null));

  // Pre-fill profile questions for returning users
  useEffect(() => {
    if (loginInput.trim()) {
      const userId = loginInput.trim();

      // First try to load from S3
      (async () => {
        try {
          const response = await fetch(`/.netlify/functions/get-latest-export?userId=${encodeURIComponent(userId)}`);

          if (response.ok) {
            const result = await response.json();

            if (result && result.data && result.data.userProfile) {
              const profile = result.data.userProfile;
              setEducationLevel(profile.educationLevel || "");
              setTradingExperience(profile.tradingExperience || "");
              setIsBusinessStudent(profile.isBusinessStudent || "");
              console.log('✅ Pre-filled profile from S3');
              return;
            }
          }
        } catch (error) {
          console.log('ℹ️ Could not load profile from S3, trying localStorage');
        }

        // Fallback to localStorage
        const userProfileKey = `htdc_userProfile_${userId}`;
        const savedProfile = storageGet<UserProfile | null>(userProfileKey, null);
        if (savedProfile) {
          setEducationLevel(savedProfile.educationLevel || "");
          setTradingExperience(savedProfile.tradingExperience || "");
          setIsBusinessStudent(savedProfile.isBusinessStudent || "");
          console.log('✅ Pre-filled profile from localStorage');
        }
      })();
    }
  }, [loginInput]);

  // Trading data and navigation
  const [tradingData, setTradingData] = useState<Map<string, CacheDay>>(new Map());
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [cacheDay, setCacheDay] = useState<CacheDay>(() => storageGet("htdc_cacheDay", MOCK_DAY));
  const [activeStep, setActiveStep] = useState<StepKey>("d0");

  // Load trading data on mount
  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        const dataPath = import.meta.env.VITE_DATA_PATH || "gpt_4o_mini/aapl/trading_data_cleaned.json";
        const data = await loadTradingData(dataPath);
        setTradingData(data);
        const dates = getAvailableDates(data);
        setAvailableDates(dates);

        // Load the first date's data
        if (dates.length > 0) {
          const firstDay = data.get(dates[0]);
          if (firstDay) {
            setCacheDay(firstDay);
          }
        }
      } catch (error) {
        console.error("Failed to load trading data:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  // Completion screen
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);

  // Decisions-in-progress per step - NO DEFAULTS, require explicit selection
  const [action, setAction] = useState<DecisionAction | null>(null);
  const [reliability, setReliability] = useState<number>(0); // Start at 0, must move above 0
  const [rationale, setRationale] = useState<string>("");
  const [dataLeakage, setDataLeakage] = useState<"yes" | "no" | null>(null);

  // Track time spent on each data source for auto-weight calculation
  const [timeSpent, setTimeSpent] = useState({ price: 0, fundamentals: 0, market: 0, news: 0, sentiment: 0 });
  const [stepStartTime, setStepStartTime] = useState<number>(Date.now());

  // Final-only extras - NO DEFAULTS
  const [weights, setWeights] = useState({ fundamentals: 25, market: 25, news: 25, sentiment: 25 });
  const [mostInfluential, setMostInfluential] = useState<DecisionRecord["mostInfluential"] | null>(null);
  const [mostReliable, setMostReliable] = useState<DecisionRecord["mostReliable"] | null>(null);
  const [tradePercentage, setTradePercentage] = useState<number>(100); // Default to 100%

  // Collected decisions
  const [records, setRecords] = useState<DecisionRecord[]>(() => storageGet("htdc_records", []));

  // Portfolio
  const [portfolio, setPortfolio] = useState<PortfolioState>(() =>
    storageGet("htdc_portfolio", {
      cash: 10_000,
      shares: 0,
      lastPrice: cacheDay.market.lastClose ?? cacheDay.price30d.at(-1)?.price ?? 0,
      history: [],
    })
  );


  useEffect(() => {
    storageSet("htdc_records", records);
  }, [records]);

  useEffect(() => {
    storageSet("htdc_cacheDay", cacheDay);
  }, [cacheDay]);

  useEffect(() => {
    storageSet("htdc_portfolio", portfolio);
  }, [portfolio]);

  // Track time spent on each step and update weights
  useEffect(() => {
    const stepToSource: Record<string, keyof typeof timeSpent | null> = {
      d0: "price",
      d1: "fundamentals",
      d2: "market",
      d3: "news",
      d4: "sentiment",
      final: null
    };

    return () => {
      // When leaving a step, record the time spent
      const source = stepToSource[activeStep];
      if (source) {
        const elapsed = Date.now() - stepStartTime;
        setTimeSpent(prev => ({
          ...prev,
          [source]: prev[source] + elapsed
        }));
      }
    };
  }, [activeStep, stepStartTime]);

  // Reset input fields when switching steps
  useEffect(() => {
    // Update step start time
    setStepStartTime(Date.now());

    // NO default values - require explicit user selection
    setAction(null);
    setReliability(0); // Start at 0, must move above 0
    setRationale("");
    setDataLeakage(null);

    // Auto-calculate weights when reaching final step
    if (activeStep === "final") {
      const total = timeSpent.fundamentals + timeSpent.market + timeSpent.news + timeSpent.sentiment;
      if (total > 0) {
        const calculatedWeights = {
          fundamentals: Math.round((timeSpent.fundamentals / total) * 100),
          market: Math.round((timeSpent.market / total) * 100),
          news: Math.round((timeSpent.news / total) * 100),
          sentiment: Math.round((timeSpent.sentiment / total) * 100),
        };

        // Adjust to ensure sum is exactly 100
        const sum = calculatedWeights.fundamentals + calculatedWeights.market + calculatedWeights.news + calculatedWeights.sentiment;
        if (sum !== 100) {
          const diff = 100 - sum;
          calculatedWeights.fundamentals += diff; // Add remainder to fundamentals
        }

        setWeights(calculatedWeights);
      }

      // ALWAYS reset mostInfluential and mostReliable to null when entering final step
      // Force user to make fresh selections each time
      setMostInfluential(null);
      setMostReliable(null);
      setTradePercentage(100); // Reset to 100% default
    }
  }, [activeStep, cacheDay.date, cacheDay.ticker]);

  const stepIndex = useMemo(() => STEPS.findIndex((s) => s.key === activeStep), [activeStep]);
  const progress = useMemo(() => ((stepIndex + 1) / STEPS.length) * 100, [stepIndex]);

  const todayPrice = useMemo(() => {
    return cacheDay.market.lastClose ?? cacheDay.price30d.at(-1)?.price ?? portfolio.lastPrice ?? 0;
  }, [cacheDay, portfolio.lastPrice]);

  const todaysValue = useMemo(() => portfolio.cash + portfolio.shares * todayPrice, [portfolio, todayPrice]);
  const startValue = useMemo(() => {
    const first = portfolio.history[0]?.value;
    return first ?? 10_000;
  }, [portfolio.history]);
  const cumReturn = useMemo(() => (todaysValue - startValue) / startValue, [todaysValue, startValue]);

  const lastHistoryValue = useMemo(() => portfolio.history.at(-1)?.value ?? startValue, [portfolio.history, startValue]);
  const dailyPnl = useMemo(() => todaysValue - lastHistoryValue, [todaysValue, lastHistoryValue]);

  function upsertRecord(rec: DecisionRecord) {
    setRecords((prev) => {
      const idx = prev.findIndex((r) => r.userId === rec.userId && r.sessionId === rec.sessionId && r.id === rec.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = rec;
        return copy;
      }
      // overwrite same (date,ticker,step,user)
      const existingIdx = prev.findIndex((r) => r.userId === rec.userId && r.date === rec.date && r.ticker === rec.ticker && r.step === rec.step);
      if (existingIdx >= 0) {
        const copy = [...prev];
        copy[existingIdx] = rec;
        return copy;
      }
      return [...prev, rec];
    });
  }

  async function handleSubmitStep() {
    // Validate data leakage question (only for d1-d4, not d0 or final)
    if (activeStep !== "d0" && activeStep !== "final" && dataLeakage === null) {
      alert("⚠️ Required: Please answer the data leakage question (Yes or No).");
      return;
    }

    // Validate Action selection
    if (action === null) {
      alert("⚠️ Required: Please select a trading action (BUY, SELL, or HOLD).");
      return;
    }

    // Validate Reliability rating (must be > 0)
    if (reliability === null || reliability <= 0) {
      alert("⚠️ Required: Please rate the decision confidence by moving the slider above 0 (range: 1-100).");
      return;
    }

    // Rationale is optional - no validation needed

    // validate final weights sum
    let sourceWeights: DecisionRecord["sourceWeights"] | undefined = undefined;
    let timeSpentData: DecisionRecord["timeSpent"] | undefined = undefined;
    let infl: DecisionRecord["mostInfluential"] | undefined = undefined;
    let rel: DecisionRecord["mostReliable"] | undefined = undefined;

    if (activeStep === "final") {
      const sum = weights.fundamentals + weights.market + weights.news + weights.sentiment;
      if (sum !== 100) {
        alert("⚠️ Error: Source weights must sum to exactly 100.");
        return;
      }

      // Validate mostInfluential selection
      if (mostInfluential === null) {
        alert("⚠️ Required: Please select which data source was most influential.");
        return;
      }

      // Validate mostReliable selection
      if (mostReliable === null) {
        alert("⚠️ Required: Please select which data source was most reliable.");
        return;
      }

      sourceWeights = weights;
      // Convert time spent from milliseconds to seconds
      timeSpentData = {
        fundamentals: Math.round(timeSpent.fundamentals / 1000),
        market: Math.round(timeSpent.market / 1000),
        news: Math.round(timeSpent.news / 1000),
        sentiment: Math.round(timeSpent.sentiment / 1000),
      };
      infl = mostInfluential;
      rel = mostReliable;
    }

    const rec: DecisionRecord = {
      id: uid("dec"),
      userId: userId.trim(),
      sessionId,
      date: cacheDay.date,
      ticker: cacheDay.ticker,
      step: activeStep,
      action,
      reliability: clamp(Math.round(reliability), 1, 100),
      rationale: rationale.trim(),
      createdAt: new Date().toISOString(),
      dataLeakage: (activeStep !== "d0" && activeStep !== "final") ? dataLeakage ?? undefined : undefined,
      sourceWeights,
      timeSpent: timeSpentData,
      mostInfluential: infl,
      mostReliable: rel,
      tradePercentage: activeStep === "final" ? tradePercentage : undefined,
    };

    upsertRecord(rec);

    // If final: apply portfolio update and auto-save to S3
    if (activeStep === "final") {
      setPortfolio((p) => {
        const price = todayPrice || 0;
        let cash = p.cash;
        let shares = p.shares;

        if (action === "BUY") {
          // BUY: Use selected percentage of available cash to buy shares
          if (price > 0 && cash >= price) {
            const cashToUse = (cash * tradePercentage) / 100;
            const sharesToBuy = Math.floor(cashToUse / price);
            if (sharesToBuy > 0) {
              shares += sharesToBuy;
              cash -= sharesToBuy * price;
            }
          }
        } else if (action === "SELL") {
          // SELL: Sell selected percentage of shares
          if (shares > 0 && price > 0) {
            const sharesToSell = Math.floor((shares * tradePercentage) / 100);
            if (sharesToSell > 0) {
              cash += sharesToSell * price;
              shares -= sharesToSell;
            }
          }
        }
        const value = cash + shares * price;
        const prevValue = p.history.at(-1)?.value ?? value;
        const pnl = value - prevValue;
        const history = [...p.history, { date: cacheDay.date, value, pnl, action, cash, shares, lastPrice: price, reliability: clamp(Math.round(reliability), 1, 100) }];
        return { cash, shares, lastPrice: price, history };
      });

      // Auto-save to S3 after final decision (non-blocking, silent)
      setTimeout(async () => {
        const payload = generateExportPayload();
        await saveToS3(payload, false); // false = don't show alert
      }, 500); // Small delay to ensure state updates are complete

      // Check if this is the last day
      const isLastDay = currentDateIndex >= availableDates.length - 1;

      if (isLastDay) {
        // Last day: automatically submit and show completion screen
        setShowCompletionScreen(true);
      } else {
        // Not last day: automatically move to next day
        navigateToNextDay();
      }
    } else {
      // Advance to next step (sequential only)
      setActiveStep(STEPS[stepIndex + 1].key);
    }
  }

  // Helper function to generate export payload
  function generateExportPayload() {
    const STEP_TO_SOURCE = Object.fromEntries(
      STEPS.map((s) => {
        const source =
          s.key === "final"
            ? "final"
            : s.label.split(" ").slice(1).join(" ").toLowerCase();
        return [s.key, source];
      })
    ) as Record<StepKey, string>;

    // Only keep user answers (decision records)
    const userRecords = records.filter((r) => r.userId === userId.trim());

    // Group answers by date
    const answersByDate: Record<string, any[]> = {};
    userRecords.forEach((r) => {
      if (!answersByDate[r.date]) {
        answersByDate[r.date] = [];
      }
      answersByDate[r.date].push({
        userId: r.userId,
        sessionId: r.sessionId,
        ticker: r.ticker,
        dataSource: STEP_TO_SOURCE[r.step],
        action: r.action,
        reliability: r.reliability,
        rationale: r.rationale,
        createdAt: r.createdAt,
        dataLeakage: r.dataLeakage,
        sourceWeights: r.sourceWeights,
        timeSpent: r.timeSpent,
        mostInfluential: r.mostInfluential,
        mostReliable: r.mostReliable,
        tradePercentage: r.tradePercentage,
      });
    });

    // Create portfolio snapshot by date
    const portfolioByDate: Record<string, any> = {};
    portfolio.history.forEach((h) => {
      portfolioByDate[h.date] = {
        cash: h.cash,
        shares: h.shares,
        lastPrice: h.lastPrice,
        totalValue: h.value,
        pnl: h.pnl,
        action: h.action,
      };
    });

    // Combine answers and portfolio data by date
    const dataByDate: Record<string, any> = {};
    Object.keys(answersByDate).forEach((date) => {
      dataByDate[date] = {
        answers: answersByDate[date],
        portfolioAfterTrading: portfolioByDate[date] || null,
      };
    });

    return {
      meta: {
        exportedAt: new Date().toISOString(),
        app: "HumanTradingDecisionCollection",
        version: "0.3",
        userId: userId.trim(),
      },
      userProfile,
      dataByDate,
      currentPortfolio: {
        cash: portfolio.cash,
        shares: portfolio.shares,
        lastPrice: portfolio.lastPrice,
        totalValue: portfolio.cash + portfolio.shares * portfolio.lastPrice,
        snapshotAt: new Date().toISOString(),
      },
    };
  }

  // Helper function to save export data to S3
  async function saveToS3(payload: any, showAlert = true) {
    try {
      const response = await fetch('/.netlify/functions/export-user-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId.trim(),
          exportData: payload,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ Export saved to S3:', result.key);
        return { success: true, key: result.key };
      } else {
        console.error('❌ Failed to save export to S3:', result.error);
        if (showAlert) {
          alert(`❌ Cloud storage failed: ${result.error}\n\nPlease contact support if this persists.`);
        }
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('❌ Error saving export to S3:', error);
      if (showAlert) {
        alert(`❌ Cloud storage unavailable\n\nPlease try again later.`);
      }
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  async function exportJSON() {
    const payload = generateExportPayload();

    // Download the file locally
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `htdc_answers_${userId.trim() || "anon"}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    // Also save to AWS S3
    const result = await saveToS3(payload);

    if (result.success) {
      alert(`✅ Export successful!\n\n📥 Downloaded locally\n☁️ Saved to cloud storage: ${result.key}`);
    } else {
      alert(`⚠️ Export partially successful:\n\n📥 Downloaded locally\n❌ Cloud storage failed: ${result.error}\n\nPlease contact support if this persists.`);
    }
  }
  

  async function handleLogin() {
    const trimmedUserId = loginInput.trim();
    if (!trimmedUserId) {
      alert("⚠️ Please enter a valid User ID");
      return;
    }

    // Prevent reserved User ID
    if (trimmedUserId.toLowerCase() === "niki01") {
      alert("⚠️ This User ID is reserved. Please choose a different User ID.");
      return;
    }

    // Validate profile questions
    if (!educationLevel) {
      alert("⚠️ Please select your education level");
      return;
    }

    if (!tradingExperience) {
      alert("⚠️ Please select your trading experience level");
      return;
    }

    if (!isBusinessStudent) {
      alert("⚠️ Please indicate if you are/were a business school student");
      return;
    }

    // Try to load the latest export from S3
    let savedRecords: DecisionRecord[] = [];
    let savedPortfolio: PortfolioState = {
      cash: 10_000,
      shares: 0,
      lastPrice: cacheDay.market.lastClose ?? cacheDay.price30d.at(-1)?.price ?? 0,
      history: [],
    };
    let savedProgress: { dateIndex: number; step: StepKey } = { dateIndex: 0, step: "d0" as StepKey };
    let existingProfile: UserProfile | null = null;

    try {
      const response = await fetch(`/.netlify/functions/get-latest-export?userId=${encodeURIComponent(trimmedUserId)}`);

      if (response.ok) {
        const result = await response.json();

        if (result && result.data) {
          console.log('✅ Loaded latest export from S3:', result.key);

          const exportData = result.data;

          // Extract user profile
          if (exportData.userProfile) {
            existingProfile = exportData.userProfile;
          }

          // Reconstruct records from dataByDate
          if (exportData.dataByDate) {
            const reconstructedRecords: DecisionRecord[] = [];

            Object.keys(exportData.dataByDate).forEach((date) => {
              const dayData = exportData.dataByDate[date];
              if (dayData.answers && Array.isArray(dayData.answers)) {
                dayData.answers.forEach((answer: any) => {
                  // Find step key from dataSource
                  let stepKey: StepKey = "d0";
                  if (answer.dataSource) {
                    const sourceMap: Record<string, StepKey> = {
                      "d0": "d0",
                      "d1": "d1",
                      "d2": "d2",
                      "d3": "d3",
                      "d4": "d4",
                      "final": "final"
                    };
                    stepKey = sourceMap[answer.dataSource] || "d0";
                  }

                  reconstructedRecords.push({
                    id: `${answer.userId}-${date}-${stepKey}`,
                    userId: answer.userId,
                    sessionId: answer.sessionId,
                    date: date,
                    ticker: answer.ticker,
                    step: stepKey,
                    action: answer.action,
                    reliability: answer.reliability,
                    rationale: answer.rationale,
                    createdAt: answer.createdAt,
                    dataLeakage: answer.dataLeakage,
                    sourceWeights: answer.sourceWeights,
                    timeSpent: answer.timeSpent,
                    mostInfluential: answer.mostInfluential,
                    mostReliable: answer.mostReliable,
                    tradePercentage: answer.tradePercentage,
                  });
                });
              }
            });

            savedRecords = reconstructedRecords;
          }

          // Reconstruct portfolio from currentPortfolio and history from dataByDate
          if (exportData.currentPortfolio) {
            savedPortfolio = {
              cash: exportData.currentPortfolio.cash,
              shares: exportData.currentPortfolio.shares,
              lastPrice: exportData.currentPortfolio.lastPrice,
              history: [],
            };

            // Rebuild portfolio history from dataByDate
            if (exportData.dataByDate) {
              const historyEntries: PortfolioState['history'] = [];
              Object.keys(exportData.dataByDate).forEach((date) => {
                const dayData = exportData.dataByDate[date];
                if (dayData.portfolioAfterTrading) {
                  // Find the reliability from the final step answer for this date
                  let reliability = 50; // default
                  if (dayData.answers && Array.isArray(dayData.answers)) {
                    const finalAnswer = dayData.answers.find((a: any) => a.dataSource === 'final');
                    if (finalAnswer && finalAnswer.reliability) {
                      reliability = finalAnswer.reliability;
                    }
                  }

                  historyEntries.push({
                    date: date,
                    cash: dayData.portfolioAfterTrading.cash,
                    shares: dayData.portfolioAfterTrading.shares,
                    lastPrice: dayData.portfolioAfterTrading.lastPrice,
                    value: dayData.portfolioAfterTrading.totalValue,
                    pnl: dayData.portfolioAfterTrading.pnl,
                    action: dayData.portfolioAfterTrading.action,
                    reliability: reliability,
                  });
                }
              });

              // Sort by date
              historyEntries.sort((a, b) => a.date.localeCompare(b.date));
              savedPortfolio.history = historyEntries;
            }
          }

          // Calculate progress: find the latest completed day
          if (savedRecords.length > 0) {
            // Group records by date
            const recordsByDate: Record<string, DecisionRecord[]> = {};
            savedRecords.forEach((r) => {
              if (!recordsByDate[r.date]) {
                recordsByDate[r.date] = [];
              }
              recordsByDate[r.date].push(r);
            });

            // Find the latest date with a "final" step
            const datesWithFinal = Object.keys(recordsByDate).filter((date) =>
              recordsByDate[date].some((r) => r.step === "final")
            );

            if (datesWithFinal.length > 0) {
              // Sort dates and get the latest
              datesWithFinal.sort();
              const latestCompletedDate = datesWithFinal[datesWithFinal.length - 1];
              const dateIndex = availableDates.indexOf(latestCompletedDate);

              if (dateIndex >= 0) {
                // Move to next day if not the last day
                if (dateIndex < availableDates.length - 1) {
                  savedProgress = { dateIndex: dateIndex + 1, step: "d0" };
                } else {
                  // Last day completed
                  savedProgress = { dateIndex: dateIndex, step: "final" };
                }
              }
            } else {
              // Find the latest date and step
              const allDates = Object.keys(recordsByDate).sort();
              if (allDates.length > 0) {
                const latestDate = allDates[allDates.length - 1];
                const latestRecords = recordsByDate[latestDate];
                const dateIndex = availableDates.indexOf(latestDate);

                // Find the latest step for this date
                const stepOrder: StepKey[] = ["d0", "d1", "d2", "d3", "d4", "final"];
                const latestStepIndex = Math.max(
                  ...latestRecords.map((r) => stepOrder.indexOf(r.step))
                );
                const latestStep = stepOrder[latestStepIndex];

                // Move to next step if possible
                if (latestStepIndex < stepOrder.length - 1) {
                  savedProgress = { dateIndex: dateIndex, step: stepOrder[latestStepIndex + 1] };
                } else {
                  savedProgress = { dateIndex: dateIndex, step: latestStep };
                }
              }
            }
          }
        } else {
          console.log('ℹ️ No export found in S3, starting fresh');
        }
      } else {
        console.log('ℹ️ Could not load from S3, starting fresh');
      }
    } catch (error) {
      console.error('❌ Failed to load from S3:', error);
      console.log('ℹ️ Starting with fresh data');
    }

    // Create or update user profile
    const profile: UserProfile = {
      educationLevel: existingProfile?.educationLevel || educationLevel,
      tradingExperience: existingProfile?.tradingExperience || tradingExperience,
      isBusinessStudent: existingProfile?.isBusinessStudent || isBusinessStudent,
      createdAt: existingProfile?.createdAt || new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
    };

    // Also save to localStorage as backup
    const userRecordsKey = `htdc_records_${trimmedUserId}`;
    const userPortfolioKey = `htdc_portfolio_${trimmedUserId}`;
    const userProgressKey = `htdc_progress_${trimmedUserId}`;
    const userProfileKey = `htdc_userProfile_${trimmedUserId}`;

    storageSet(userRecordsKey, savedRecords);
    storageSet(userPortfolioKey, savedPortfolio);
    storageSet(userProgressKey, savedProgress);
    storageSet(userProfileKey, profile);

    setUserId(trimmedUserId);
    setRecords(savedRecords);
    setPortfolio(savedPortfolio);
    setCurrentDateIndex(savedProgress.dateIndex);
    setActiveStep(savedProgress.step);
    setUserProfile(profile);
    setIsLoggedIn(true);
    setLoginInput("");
    setEducationLevel("");
    setTradingExperience("");
    setIsBusinessStudent("");

    // Load the saved day's data
    if (availableDates.length > 0 && savedProgress.dateIndex < availableDates.length) {
      const savedDate = availableDates[savedProgress.dateIndex];
      const savedDayData = tradingData.get(savedDate);
      if (savedDayData) {
        setCacheDay(savedDayData);
      }
    }

    // Check if all days are completed and show completion screen
    if (savedProgress.dateIndex >= availableDates.length - 1 && savedProgress.step === "final") {
      setShowCompletionScreen(true);
    }
  }

  async function handleLogout() {
    if (!confirm("Are you sure you want to logout? Your data will be saved to cloud storage before logout.")) return;

    // Save current progress to localStorage
    saveUserProgress();

    // Save to S3 before logout
    const payload = generateExportPayload();
    await saveToS3(payload, true);

    setIsLoggedIn(false);
    setLoginInput("");
  }

  function saveUserProgress() {
    if (!userId.trim()) return;

    const userRecordsKey = `htdc_records_${userId.trim()}`;
    const userPortfolioKey = `htdc_portfolio_${userId.trim()}`;
    const userProgressKey = `htdc_progress_${userId.trim()}`;

    storageSet(userRecordsKey, records);
    storageSet(userPortfolioKey, portfolio);
    storageSet(userProgressKey, {
      dateIndex: currentDateIndex,
      step: activeStep,
    });
  }

  // Auto-save progress when key data changes
  useEffect(() => {
    if (isLoggedIn && userId.trim()) {
      saveUserProgress();
    }
  }, [records, portfolio, currentDateIndex, activeStep]);

  async function resetAll() {
    if (!confirm("Are you sure you want to reset all data? Your current progress will be saved to cloud storage before reset.")) return;

    // Save current data to S3 before resetting
    const payload = generateExportPayload();
    await saveToS3(payload, true);

    const userRecordsKey = `htdc_records_${userId.trim()}`;
    const userPortfolioKey = `htdc_portfolio_${userId.trim()}`;
    const userProgressKey = `htdc_progress_${userId.trim()}`;

    setRecords([]);
    setPortfolio({ cash: 10_000, shares: 0, lastPrice: todayPrice ?? 0, history: [] });
    setCurrentDateIndex(0);
    setActiveStep("d0");

    storageSet(userRecordsKey, []);
    storageSet(userPortfolioKey, { cash: 10_000, shares: 0, lastPrice: todayPrice ?? 0, history: [] });
    storageSet(userProgressKey, { dateIndex: 0, step: "d0" });
  }

  function navigateToPreviousDay() {
    if (currentDateIndex > 0) {
      const newIndex = currentDateIndex - 1;
      const newDate = availableDates[newIndex];
      const newDayData = tradingData.get(newDate);
      if (newDayData) {
        setCacheDay(newDayData);
        setCurrentDateIndex(newIndex);
        setActiveStep("d0"); // Reset to first step for new day
        setTimeSpent({ price: 0, fundamentals: 0, market: 0, news: 0, sentiment: 0 }); // Reset time tracking
      }
    }
  }

  function navigateToNextDay() {
    if (currentDateIndex < availableDates.length - 1) {
      const newIndex = currentDateIndex + 1;
      const newDate = availableDates[newIndex];
      const newDayData = tradingData.get(newDate);
      if (newDayData) {
        setCacheDay(newDayData);
        setCurrentDateIndex(newIndex);
        setActiveStep("d0"); // Reset to first step for new day
        setTimeSpent({ price: 0, fundamentals: 0, market: 0, news: 0, sentiment: 0 }); // Reset time tracking
      }
    }
  }

  function selectDate(dateStr: string) {
    const newIndex = availableDates.indexOf(dateStr);
    if (newIndex >= 0) {
      const newDayData = tradingData.get(dateStr);
      if (newDayData) {
        setCacheDay(newDayData);
        setCurrentDateIndex(newIndex);
        setActiveStep("d0");
        setTimeSpent({ price: 0, fundamentals: 0, market: 0, news: 0, sentiment: 0 }); // Reset time tracking
      }
    }
  }

  const stepDone = useMemo(() => {
    const dayRecords = records
      .filter((r) => r.userId === userId.trim())
      .filter((r) => r.date === cacheDay.date && r.ticker === cacheDay.ticker);
    const set = new Set(dayRecords.map((r) => r.step));
    return set;
  }, [records, userId, cacheDay.date, cacheDay.ticker]);

  // Determine which step should be accessible (sequential unlock)
  const currentAllowedStep = useMemo(() => {
    // Find the first incomplete step
    for (const step of STEPS) {
      if (!stepDone.has(step.key)) {
        return step.key;
      }
    }
    // All steps done, stay on final
    return "final";
  }, [stepDone]);

  // Enforce sequential navigation - lock to current allowed step
  useEffect(() => {
    if (activeStep !== currentAllowedStep) {
      setActiveStep(currentAllowedStep);
    }
  }, [currentAllowedStep]);

  const markedDate = cacheDay.date;

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen w-full bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-lg"
        >
          <Card className="rounded-2xl shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Human Trading Decision Collection</CardTitle>
              <CardDescription>Please login to continue or resume your session</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* User ID Input */}
              <div className="space-y-2">
                <Label htmlFor="login-input">User ID <span className="text-destructive">*</span></Label>
                <Input
                  id="login-input"
                  value={loginInput}
                  onChange={(e) => setLoginInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleLogin();
                    }
                  }}
                  placeholder="Enter your User ID (e.g., Alice01)"
                  className="h-12 text-base"
                  autoFocus
                />
                <div className="text-xs text-muted-foreground">
                  Your progress is automatically saved. Login with the same User ID to resume.
                </div>
              </div>

              {/* Education Level Question */}
              <div className="space-y-2">
                <Label>Education Level <span className="text-destructive">*</span></Label>
                <RadioGroup value={educationLevel} onValueChange={setEducationLevel}>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="high-school" id="edu-hs" />
                      <Label htmlFor="edu-hs" className="cursor-pointer font-normal">
                        High School
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="some-college" id="edu-sc" />
                      <Label htmlFor="edu-sc" className="cursor-pointer font-normal">
                        Some College (no degree)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="associate" id="edu-as" />
                      <Label htmlFor="edu-as" className="cursor-pointer font-normal">
                        Associate Degree
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="bachelor" id="edu-ba" />
                      <Label htmlFor="edu-ba" className="cursor-pointer font-normal">
                        Bachelor's Degree
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="master" id="edu-ma" />
                      <Label htmlFor="edu-ma" className="cursor-pointer font-normal">
                        Master's Degree
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="doctorate" id="edu-phd" />
                      <Label htmlFor="edu-phd" className="cursor-pointer font-normal">
                        Doctorate (PhD, MD, JD, etc.)
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Trading Experience Question */}
              <div className="space-y-2">
                <Label>Trading Experience Level <span className="text-destructive">*</span></Label>
                <RadioGroup value={tradingExperience} onValueChange={setTradingExperience}>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="1-none" id="exp-1" />
                      <Label htmlFor="exp-1" className="cursor-pointer font-normal">
                        Level 1 - No experience (Never traded stocks/securities)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="2-beginner" id="exp-2" />
                      <Label htmlFor="exp-2" className="cursor-pointer font-normal">
                        Level 2 - Beginner (Some knowledge, minimal trading)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="3-intermediate" id="exp-3" />
                      <Label htmlFor="exp-3" className="cursor-pointer font-normal">
                        Level 3 - Intermediate (Regular trader, 1-3 years experience)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="4-advanced" id="exp-4" />
                      <Label htmlFor="exp-4" className="cursor-pointer font-normal">
                        Level 4 - Advanced (Active trader, 3-5 years experience)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="5-expert" id="exp-5" />
                      <Label htmlFor="exp-5" className="cursor-pointer font-normal">
                        Level 5 - Expert (Professional/Extensive experience, 5+ years)
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Business School Question */}
              <div className="space-y-2">
                <Label>Business School Background <span className="text-destructive">*</span></Label>
                <RadioGroup value={isBusinessStudent} onValueChange={setIsBusinessStudent}>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="current" id="bs-current" />
                      <Label htmlFor="bs-current" className="cursor-pointer font-normal">
                        Currently a business school student
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="former" id="bs-former" />
                      <Label htmlFor="bs-former" className="cursor-pointer font-normal">
                        Former business school student/graduate
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="bs-no" />
                      <Label htmlFor="bs-no" className="cursor-pointer font-normal">
                        No business school background
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Login Button */}
              <Button onClick={handleLogin} className="w-full h-12 text-base" size="lg">
                Login
              </Button>

              {/* Info Box */}
              <div className="rounded-xl border bg-blue-50 dark:bg-blue-950 p-3">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-900 dark:text-blue-100">
                    <div className="font-semibold mb-1">New User?</div>
                    Simply enter a unique User ID and answer the questions above.
                    <div className="font-semibold mt-2 mb-1">Returning User?</div>
                    Enter your previous User ID. Your profile is already saved.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Completion screen
  if (showCompletionScreen) {
    const finalValue = portfolio.cash + portfolio.shares * todayPrice;
    const initialValue = 10_000;
    const totalReturn = ((finalValue - initialValue) / initialValue) * 100;
    const totalDays = availableDates.length;
    const completedDays = portfolio.history.length;

    return (
      <div className="min-h-screen w-full bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-4xl"
        >
          <Card className="rounded-2xl shadow-lg">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-4">
                <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <span className="text-4xl">🎉</span>
                </div>
              </div>
              <CardTitle className="text-3xl">Trading Study Completed!</CardTitle>
              <CardDescription className="text-base mt-2">
                Thank you for completing all {totalDays} trading days. Your results have been saved.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="rounded-xl">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="text-sm text-muted-foreground mb-1">Total Return</div>
                      <div className={`text-3xl font-bold ${totalReturn >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(2)}%
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="text-sm text-muted-foreground mb-1">Final Portfolio Value</div>
                      <div className="text-3xl font-bold">{fmtMoney(finalValue)}</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="text-sm text-muted-foreground mb-1">Trading Days Completed</div>
                      <div className="text-3xl font-bold">{completedDays} / {totalDays}</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Portfolio Breakdown */}
              <Card className="rounded-xl">
                <CardHeader>
                  <CardTitle className="text-lg">Final Portfolio Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Cash</span>
                      <span className="font-semibold">{fmtMoney(portfolio.cash)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Shares ({cacheDay.ticker})</span>
                      <span className="font-semibold">{portfolio.shares} shares</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Share Value (@ {fmtMoney(todayPrice)})</span>
                      <span className="font-semibold">{fmtMoney(portfolio.shares * todayPrice)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 bg-muted/30 rounded-lg px-3 mt-2">
                      <span className="font-semibold">Total Value</span>
                      <span className="text-xl font-bold">{fmtMoney(finalValue)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Performance Summary */}
              <Card className="rounded-xl">
                <CardHeader>
                  <CardTitle className="text-lg">Performance Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Initial Investment</span>
                      <span className="font-medium">{fmtMoney(initialValue)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Final Value</span>
                      <span className="font-medium">{fmtMoney(finalValue)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="font-semibold">Total P&L</span>
                      <span className={`font-bold ${finalValue - initialValue >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {finalValue - initialValue >= 0 ? '+' : ''}{fmtMoney(finalValue - initialValue)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  className="flex-1 h-12 text-base gap-2"
                  onClick={async () => {
                    const payload = generateExportPayload();
                    const result = await saveToS3(payload);
                    if (result.success) {
                      alert(`✅ Data exported successfully!\n\n☁️ Saved to cloud storage: ${result.key}`);
                    }
                  }}
                >
                  <Download className="h-4 w-4" />
                  Export Results
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-12 text-base gap-2"
                  onClick={() => {
                    setShowCompletionScreen(false);
                  }}
                >
                  View Portfolio History
                </Button>
              </div>

              {/* Info Box */}
              <div className="rounded-xl border bg-blue-50 dark:bg-blue-950 p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900 dark:text-blue-100">
                    <div className="font-semibold mb-2">Your data has been saved</div>
                    <p>All your trading decisions and portfolio history have been automatically saved to cloud storage. You can safely logout or close this window.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-4 flex flex-col gap-3 md:mb-6 md:flex-row md:items-center md:justify-between"
        >
          <div>
            <div className="text-2xl font-semibold tracking-tight">Human Trading Decision Collection</div>
            <div className="text-sm text-muted-foreground">Sequential decision logging (d0→d1→d2→d3→d4→Final)</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-2xl border bg-muted/20 px-3 py-2">
              <Label className="text-xs">Logged in as</Label>
              <Badge variant="default" className="rounded-xl font-mono">
                {userId}
              </Badge>
              <Badge variant="secondary" className="rounded-xl">
                {cacheDay.ticker} · {cacheDay.date}
              </Badge>
            </div>
            <Button variant="outline" className="gap-2" onClick={exportJSON}>
              <Download className="h-4 w-4" /> Export
            </Button>
            <Button variant="outline" className="gap-2" onClick={resetAll}>
              <RefreshCw className="h-4 w-4" /> Reset
            </Button>
            <Button variant="outline" className="gap-2" onClick={handleLogout}>
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </motion.div>

        {/* Day Progress Indicator */}
        {!isLoading && availableDates.length > 0 && (
          <Card className="mb-4 rounded-2xl">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">Trading Day {currentDateIndex + 1} of {availableDates.length}</div>
                    <div className="text-xs text-muted-foreground">
                      {cacheDay.date} • {cacheDay.ticker}
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border bg-blue-50 dark:bg-blue-950 px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <div className="text-xs text-blue-900 dark:text-blue-100">
                      Complete all steps (d0→d1→d2→d3→d4→Final) to auto-advance to next day
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isLoading && (
          <Card className="mb-4 rounded-2xl">
            <CardContent className="py-12 text-center">
              <div className="text-muted-foreground">Loading trading data...</div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Left: Interactive window */}
          <div className="lg:col-span-2">
            <Card className="rounded-2xl shadow-sm">
              <CardHeader className="space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg">Decision Workspace</CardTitle>
                    <CardDescription>
                      Review data sources sequentially. Submit after each step to log intermediate decisions.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="rounded-xl" variant={stepDone.has(activeStep) ? "default" : "secondary"}>
                      {activeStep.toUpperCase()}
                    </Badge>
                    <Badge className="rounded-xl" variant="outline">
                      {stepIndex + 1}/{STEPS.length}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Progress value={progress} className="h-2" />
                  <div className="text-xs text-muted-foreground">{Math.round(progress)}%</div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Tabs value={activeStep}>
                  <TabsList className="grid w-full grid-cols-6">
                    {STEPS.map((s, idx) => {
                      const isLocked = s.key !== currentAllowedStep && !stepDone.has(s.key);
                      const isCompleted = stepDone.has(s.key);
                      return (
                        <TabsTrigger
                          key={s.key}
                          value={s.key}
                          className="gap-1"
                          disabled={isLocked}
                          style={{ opacity: isLocked ? 0.4 : 1, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                        >
                          <span className="hidden sm:inline">{s.label}</span>
                          <span className="sm:hidden">{s.key.toUpperCase()}</span>
                          {isCompleted ? <span className="ml-1">✓</span> : isLocked ? <span className="ml-1">🔒</span> : null}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>

                  <TabsContent value="d0" className="mt-4 space-y-4">
                    <SectionHint>
                      Review the price trends below to make your decision. Base your decision ONLY on the price chart data shown.
                    </SectionHint>
                    <div className="rounded-xl border-2 border-orange-500 bg-orange-50 dark:bg-orange-950 p-4">
                      <div className="flex items-start gap-2">
                        <Info className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-1">
                            ⚠️ Important: Do NOT use supplemental information
                          </div>
                          <div className="text-xs text-orange-800 dark:text-orange-200">
                            Base your decision ONLY on the price trends shown in the charts on the right. Do not use any external sources or prior knowledge.
                          </div>
                        </div>
                      </div>
                    </div>
                    <Card className="rounded-2xl">
                      <CardHeader>
                        <CardTitle className="text-base">Available Data: Price Trends Only</CardTitle>
                        <CardDescription>Refer to the charts on the right panel for {cacheDay.ticker} and market indices trends</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="rounded-xl border bg-blue-50 dark:bg-blue-950 p-4">
                          <div className="flex items-start gap-2">
                            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-900 dark:text-blue-100">
                              <p className="font-semibold mb-2">Data available for this decision:</p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>{cacheDay.ticker} 30-day price trend (right panel)</li>
                                <li>Market Indices 30-day trends: NASDAQ, Dow Jones, S&P 500 (right panel)</li>
                              </ul>
                              <p className="mt-3">
                                Review these charts carefully to identify patterns, trends, and potential signals for your trading decision.
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="d1" className="mt-4 space-y-4">
                    <SectionHint>
                      Fundamentals: judge quality/completeness and how much you trust these metrics.
                    </SectionHint>
                    <div className="rounded-xl border-2 border-orange-500 bg-orange-50 dark:bg-orange-950 p-4">
                      <div className="flex items-start gap-2">
                        <Info className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-1">
                            ⚠️ Important: Do NOT use supplemental information
                          </div>
                          <div className="text-xs text-orange-800 dark:text-orange-200">
                            Base your decision ONLY on the fundamentals data shown below. Do not use any external sources, prior knowledge, or information from other tabs.
                          </div>
                        </div>
                      </div>
                    </div>
                    <Card className="rounded-2xl">
                      <CardHeader>
                        <CardTitle className="text-base">Fundamentals</CardTitle>
                        <CardDescription>{cacheDay.fundamentals.companyName ?? ""}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="rounded-xl border p-3">
                            <KeyValue k="Market cap" v={cacheDay.fundamentals.marketCap ?? "—"} />
                            <KeyValue k="Price-to-Earnings (P/E) Ratio" v={cacheDay.fundamentals.pe ?? "—"} />
                            <KeyValue k="Earnings Per Share (EPS)" v={cacheDay.fundamentals.eps ?? "—"} />
                            <KeyValue k="Revenue (TTM)" v={cacheDay.fundamentals.revenueTTM ?? "—"} />
                            <KeyValue
                              k="Gross margin"
                              v={
                                cacheDay.fundamentals.grossMargin !== undefined
                                  ? pct(cacheDay.fundamentals.grossMargin)
                                  : "—"
                              }
                            />
                          </div>
                          <div className="rounded-xl border p-3">
                            <div className="mb-2 text-sm font-medium">Notes</div>
                            {cacheDay.fundamentals.notesMarkdown ? (
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  h3: (props) => (
                                    <h3 className="mt-3 mb-2 text-sm font-semibold" {...props} />
                                  ),
                                  h4: (props) => (
                                    <h4 className="mt-2 mb-1 text-xs font-semibold" {...props} />
                                  ),
                                  p: (props) => (
                                    <p className="mb-2 leading-relaxed text-sm text-muted-foreground" {...props} />
                                  ),
                                  strong: (props) => (
                                    <strong className="font-semibold text-foreground" {...props} />
                                  ),
                                  ul: (props) => (
                                    <ul className="ml-4 list-disc space-y-1 text-sm text-muted-foreground" {...props} />
                                  ),
                                  li: (props) => (
                                    <li className="leading-relaxed" {...props} />
                                  ),
                                }}
                              >
                                {cacheDay.fundamentals.notesMarkdown}
                              </ReactMarkdown>
                            ) : (
                              <div className="text-sm text-muted-foreground">No notes available.</div>
                            )}
                          </div>
                        </div>
                        {cacheDay.fundamentals.raw && (
                          <details className="mt-4" open>
                            <summary className="cursor-pointer text-sm font-medium text-primary">
                              View Full Fundamentals Report
                            </summary>
                            <div className="mt-3 max-h-96 overflow-y-auto rounded-xl border bg-muted/30 p-4 text-xs">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  h3: ({ ...props }) => (
                                    <h3 className="mt-3 mb-2 text-sm font-semibold" {...props} />
                                  ),
                                  h4: ({ ...props }) => (
                                    <h4 className="mt-2 mb-1 text-xs font-semibold" {...props} />
                                  ),
                                  p: ({ ...props }) => (
                                    <p className="mb-2 leading-relaxed" {...props} />
                                  ),
                                  strong: ({ ...props }) => (
                                    <strong className="font-semibold" {...props} />
                                  ),
                                  ul: ({ ...props }) => (
                                    <ul className="ml-4 list-disc space-y-1" {...props} />
                                  ),
                                  li: ({ ...props }) => (
                                    <li className="leading-relaxed" {...props} />
                                  ),
                                }}
                              >
                                {cacheDay.fundamentals.raw}
                              </ReactMarkdown>
                            </div>
                          </details>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="d2" className="mt-4 space-y-4">
                    <SectionHint>Market: consider timeliness/accuracy of the current prices.</SectionHint>
                    <div className="rounded-xl border-2 border-orange-500 bg-orange-50 dark:bg-orange-950 p-4">
                      <div className="flex items-start gap-2">
                        <Info className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-1">
                            ⚠️ Important: Do NOT use supplemental information
                          </div>
                          <div className="text-xs text-orange-800 dark:text-orange-200">
                            Base your decision ONLY on the market data shown below. Do not use any external sources, prior knowledge, or information from other tabs.
                          </div>
                        </div>
                      </div>
                    </div>
                    <Card className="rounded-2xl">
                      <CardHeader>
                        <CardTitle className="text-base">Market</CardTitle>
                        <CardDescription>Price snapshot</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="rounded-xl border p-3">
                            <KeyValue k="Last close" v={cacheDay.market.lastClose ?? "—"} />
                            <KeyValue k="Open" v={cacheDay.market.open ?? "—"} />
                            <KeyValue k="Volume" v={cacheDay.market.volume?.toLocaleString() ?? "—"} />
                          </div>
                        </div>
                        {cacheDay.market.raw && (
                          <details className="mt-4" open>
                            <summary className="cursor-pointer text-sm font-medium text-primary">
                              View Full Market Report
                            </summary>
                            <div className="mt-3 max-h-96 overflow-y-auto rounded-xl border bg-muted/30 p-4 text-xs">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  h3: ({ ...props }) => (
                                    <h3 className="mt-3 mb-2 text-sm font-semibold" {...props} />
                                  ),
                                  h4: ({ ...props }) => (
                                    <h4 className="mt-2 mb-1 text-xs font-semibold" {...props} />
                                  ),
                                  p: ({ ...props }) => (
                                    <p className="mb-2 leading-relaxed" {...props} />
                                  ),
                                  strong: ({ ...props }) => (
                                    <strong className="font-semibold" {...props} />
                                  ),
                                  ul: ({ ...props }) => (
                                    <ul className="ml-4 list-disc space-y-1" {...props} />
                                  ),
                                  li: ({ ...props }) => (
                                    <li className="leading-relaxed" {...props} />
                                  ),
                                }}
                              >
                                {cacheDay.market.raw}
                              </ReactMarkdown>
                            </div>
                          </details>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="d3" className="mt-4 space-y-4">
                    <SectionHint>News: consider credibility of sources, bias, and verification.</SectionHint>
                    <div className="rounded-xl border-2 border-orange-500 bg-orange-50 dark:bg-orange-950 p-4">
                      <div className="flex items-start gap-2">
                        <Info className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-1">
                            ⚠️ Important: Do NOT use supplemental information
                          </div>
                          <div className="text-xs text-orange-800 dark:text-orange-200">
                            Base your decision ONLY on the news data shown below. Do not use any external sources, prior knowledge, or information from other tabs.
                          </div>
                        </div>
                      </div>
                    </div>
                    <Card className="rounded-2xl">
                      <CardHeader>
                        <CardTitle className="text-base">News</CardTitle>
                        <CardDescription>Recent articles & announcements</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {cacheDay.news.items.length === 0 ? (
                            <div className="text-sm text-muted-foreground">No news items.</div>
                          ) : (
                            cacheDay.news.items.map((n, i) => (
                              <div key={i} className="rounded-xl border p-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="text-sm font-semibold">{n.title}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {n.source ? `${n.source} · ` : ""}
                                    {n.time ?? ""}
                                  </div>
                                </div>
                                {n.summary ? <div className="mt-2 text-sm text-muted-foreground">{n.summary}</div> : null}
                                {n.url ? (
                                  <a className="mt-2 inline-block text-xs underline" href={n.url} target="_blank" rel="noreferrer">
                                    Open
                                  </a>
                                ) : null}
                              </div>
                            ))
                          )}
                        </div>
                        {cacheDay.news.raw && (
                          <details className="mt-4" open>
                            <summary className="cursor-pointer text-sm font-medium text-primary">
                              View Full News Report
                            </summary>
                            <div className="mt-3 max-h-96 overflow-y-auto rounded-xl border bg-muted/30 p-4 text-xs">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  h3: ({ ...props }) => (
                                    <h3 className="mt-3 mb-2 text-sm font-semibold" {...props} />
                                  ),
                                  h4: ({ ...props }) => (
                                    <h4 className="mt-2 mb-1 text-xs font-semibold" {...props} />
                                  ),
                                  p: ({ ...props }) => (
                                    <p className="mb-2 leading-relaxed" {...props} />
                                  ),
                                  strong: ({ ...props }) => (
                                    <strong className="font-semibold" {...props} />
                                  ),
                                  ul: ({ ...props }) => (
                                    <ul className="ml-4 list-disc space-y-1" {...props} />
                                  ),
                                  li: ({ ...props }) => (
                                    <li className="leading-relaxed" {...props} />
                                  ),
                                }}
                              >
                                {cacheDay.news.raw}
                              </ReactMarkdown>
                            </div>
                          </details>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="d4" className="mt-4 space-y-4">
                    <SectionHint>Sentiment: judge sample size, representativeness, and potential noise.</SectionHint>
                    <div className="rounded-xl border-2 border-orange-500 bg-orange-50 dark:bg-orange-950 p-4">
                      <div className="flex items-start gap-2">
                        <Info className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-1">
                            ⚠️ Important: Do NOT use supplemental information
                          </div>
                          <div className="text-xs text-orange-800 dark:text-orange-200">
                            Base your decision ONLY on the sentiment data shown below. Do not use any external sources, prior knowledge, or information from other tabs.
                          </div>
                        </div>
                      </div>
                    </div>
                    <Card className="rounded-2xl">
                      <CardHeader>
                        <CardTitle className="text-base">Sentiment</CardTitle>
                        <CardDescription>Analyst + social signals</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="rounded-xl border p-3">
                            <KeyValue k="Sentiment score" v={cacheDay.sentiment.score ?? "—"} />
                            <KeyValue k="Analyst rating" v={cacheDay.sentiment.analystRating ?? "—"} />
                          </div>
                          <div className="rounded-xl border p-3">
                            <div className="mb-2 text-sm font-medium">Social</div>
                            {(cacheDay.sentiment.social ?? []).map((s, i) => (
                              <div key={i} className="py-1">
                                <div className="flex items-center justify-between">
                                  <div className="text-sm">{s.platform}</div>
                                  <div className="text-sm font-medium">
                                    {s.score} {s.sample ? <span className="text-xs text-muted-foreground">(n={s.sample})</span> : null}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {cacheDay.sentiment.raw && (
                          <details className="mt-4" open>
                            <summary className="cursor-pointer text-sm font-medium text-primary">
                              View Full Sentiment Report
                            </summary>
                            <div className="mt-3 max-h-96 overflow-y-auto rounded-xl border bg-muted/30 p-4 text-xs">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  h3: ({ ...props }) => (
                                    <h3 className="mt-3 mb-2 text-sm font-semibold" {...props} />
                                  ),
                                  h4: ({ ...props }) => (
                                    <h4 className="mt-2 mb-1 text-xs font-semibold" {...props} />
                                  ),
                                  p: ({ ...props }) => (
                                    <p className="mb-2 leading-relaxed" {...props} />
                                  ),
                                  strong: ({ ...props }) => (
                                    <strong className="font-semibold" {...props} />
                                  ),
                                  ul: ({ ...props }) => (
                                    <ul className="ml-4 list-disc space-y-1" {...props} />
                                  ),
                                  li: ({ ...props }) => (
                                    <li className="leading-relaxed" {...props} />
                                  ),
                                }}
                              >
                                {cacheDay.sentiment.raw}
                              </ReactMarkdown>
                            </div>
                          </details>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="final" className="mt-4 space-y-4">
                    <SectionHint>
                      Final: consolidate all sources. Provide overall reliability and (optional) source weights.
                    </SectionHint>

                    <Card className="rounded-2xl">
                      <CardHeader>
                        <CardTitle className="text-base">Source weight allocation (auto-calculated)</CardTitle>
                        <CardDescription>
                          Weights are automatically calculated based on time spent reviewing each data source.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {(["fundamentals", "market", "news", "sentiment"] as const).map((k) => (
                          <div key={k} className="space-y-2 rounded-xl border p-3 bg-muted/20">
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium capitalize">{k}</div>
                              <div className="text-sm font-semibold">{(weights as any)[k]}%</div>
                            </div>
                            <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
                              <div
                                className="h-full bg-primary transition-all"
                                style={{ width: `${(weights as any)[k]}%` }}
                              />
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Time spent: {Math.round((timeSpent as any)[k] / 1000)}s
                            </div>
                          </div>
                        ))}
                        <div className="rounded-xl border bg-blue-50 dark:bg-blue-950 p-3">
                          <div className="flex items-center gap-2">
                            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            <div className="text-xs text-blue-900 dark:text-blue-100">
                              Weights are calculated based on the time you spent reviewing each data source (d1-d4).
                              The more time you spend on a source, the higher its weight.
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="rounded-2xl">
                      <CardHeader>
                        <CardTitle className="text-base">Most influential / most reliable</CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className={`rounded-xl border p-3 ${mostInfluential === null ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-950/20' : ''}`}>
                          <div className="mb-2 flex items-center justify-between">
                            <div className="text-sm font-medium">Most influential source <span className="text-destructive">*</span></div>
                            {mostInfluential === null && <span className="text-xs text-orange-600 dark:text-orange-400">Required</span>}
                          </div>
                          <RadioGroup value={mostInfluential ?? undefined} onValueChange={(v) => setMostInfluential(v as any)}>
                            {(["price trend", "market indices trend", "fundamentals", "market", "news", "sentiment"] as const).map((v) => (
                              <div key={v} className="flex items-center gap-2 py-1">
                                <RadioGroupItem value={v} id={`infl_${v}`} />
                                <Label htmlFor={`infl_${v}`} className="capitalize cursor-pointer">
                                  {v === "price trend" ? "Price Trend" : v === "market indices trend" ? "Market Indices Trend" : v}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                        <div className={`rounded-xl border p-3 ${mostReliable === null ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-950/20' : ''}`}>
                          <div className="mb-2 flex items-center justify-between">
                            <div className="text-sm font-medium">Most reliable source <span className="text-destructive">*</span></div>
                            {mostReliable === null && <span className="text-xs text-orange-600 dark:text-orange-400">Required</span>}
                          </div>
                          <RadioGroup value={mostReliable ?? undefined} onValueChange={(v) => setMostReliable(v as any)}>
                            {(["price trend", "market indices trend", "fundamentals", "market", "news", "sentiment"] as const).map((v) => (
                              <div key={v} className="flex items-center gap-2 py-1">
                                <RadioGroupItem value={v} id={`rel_${v}`} />
                                <Label htmlFor={`rel_${v}`} className="capitalize cursor-pointer">
                                  {v === "price trend" ? "Price Trend" : v === "market indices trend" ? "Market Indices Trend" : v}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>

                {/* Data Leakage Question - Only for d1-d4 */}
                {activeStep !== "d0" && activeStep !== "final" && (
                  <Card className={`rounded-2xl ${dataLeakage === null ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-950/20' : ''}`}>
                    <CardHeader>
                      <CardTitle className="text-base">Data Leakage Check <span className="text-destructive">*</span></CardTitle>
                      <CardDescription>
                        Does this data source contain information that would not have been available at the time of the decision?
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <RadioGroup value={dataLeakage ?? ""} onValueChange={(v) => setDataLeakage(v as "yes" | "no")}>
                        <div className="flex gap-4">
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="yes" id="leak-yes" />
                            <Label htmlFor="leak-yes" className="cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="no" id="leak-no" />
                            <Label htmlFor="leak-no" className="cursor-pointer">No</Label>
                          </div>
                        </div>
                      </RadioGroup>
                      {dataLeakage === null && (
                        <div className="mt-2 text-xs text-orange-600 dark:text-orange-400">
                          Please answer this question before submitting
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Decision panel */}
                <Card className="rounded-2xl border-dashed">
                  <CardHeader>
                    <CardTitle className="text-base">Decision input</CardTitle>
                    <CardDescription>
                      Submit after each step to record intermediate decisions (action + reliability + rationale).
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className={`rounded-xl border p-3 ${action === null ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-950/20' : ''}`}>
                        <div className="mb-2 flex items-center justify-between">
                          <div className="text-sm font-medium">Action <span className="text-destructive">*</span></div>
                          {action === null && <span className="text-xs text-orange-600 dark:text-orange-400">Required</span>}
                        </div>
                        <RadioGroup key={`action_${cacheDay.date}_${activeStep}`} value={action ?? ""} onValueChange={(v) => setAction(v as DecisionAction)}>
                          {(["BUY", "SELL", "HOLD"] as const).map((v) => (
                            <div key={v} className="flex items-center gap-2 py-1">
                              <RadioGroupItem value={v} id={`act_${v}`} />
                              <Label htmlFor={`act_${v}`} className="cursor-pointer">{v}</Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                      <div className={`rounded-xl border p-3 ${reliability === null || reliability === 0 ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-950/20' : ''}`}>
                        <div className="mb-2 flex items-center justify-between">
                          <div className="text-sm font-medium">Decision confidence (0–100) <span className="text-destructive">*</span></div>
                          <div className="text-sm font-semibold">{reliability !== null ? Math.round(reliability) : 0}</div>
                        </div>
                        {(reliability === null || reliability === 0) && (
                          <div className="mb-2 text-xs text-orange-600 dark:text-orange-400">Move slider above 0 to set value</div>
                        )}
                        <Slider
                          value={reliability !== null ? [reliability] : [0]}
                          min={0}
                          max={100}
                          step={1}
                          onValueChange={(v) => setReliability(v[0])}
                        />
                        <div className="mt-2 text-xs text-muted-foreground">
                          Rate the confidence of your decision based on the provided info (must be &gt; 0).
                        </div>
                      </div>
                    </div>

                    {/* Trade Percentage Selection - Only on final step and when action is BUY or SELL */}
                    {activeStep === "final" && action && action !== "HOLD" && (
                      <div className="rounded-xl border p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <div className="text-sm font-medium">Trade Percentage</div>
                          <div className="text-sm font-semibold">{tradePercentage}%</div>
                        </div>
                        <RadioGroup value={tradePercentage.toString()} onValueChange={(v) => setTradePercentage(Number(v))}>
                          <div className="grid grid-cols-4 gap-2">
                            {[25, 50, 75, 100].map((pct) => (
                              <div key={pct} className="flex items-center gap-2">
                                <RadioGroupItem value={pct.toString()} id={`pct_${pct}`} />
                                <Label htmlFor={`pct_${pct}`} className="cursor-pointer">{pct}%</Label>
                              </div>
                            ))}
                          </div>
                        </RadioGroup>
                        <div className="mt-2 text-xs text-muted-foreground">
                          {action === "BUY"
                            ? `Select what percentage of your cash (${fmtMoney(portfolio.cash)}) to use for buying shares.`
                            : `Select what percentage of your shares (${portfolio.shares}) to sell.`
                          }
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-sm font-medium">
                        Rationale / notes <span className="text-xs text-muted-foreground">(optional)</span>
                      </Label>
                      <Textarea
                        value={rationale}
                        onChange={(e) => setRationale(e.target.value)}
                        placeholder="Optionally explain why you chose this action…"
                        className="mt-2 min-h-[90px]"
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button className="gap-2" onClick={handleSubmitStep} size="lg">
                        {activeStep !== "final" ? "Submit & Continue" : "Submit Final Decision"}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    {activeStep !== "final" && (
                      <div className="rounded-xl border bg-blue-50 dark:bg-blue-950 p-3">
                        <div className="flex items-center gap-2">
                          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                          <div className="text-xs text-blue-900 dark:text-blue-100">
                            Sequential flow: Submit this step to unlock the next data source. You cannot go back or edit previous decisions.
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </div>

          {/* Right: Price charts + Portfolio */}
          <div className="space-y-4">
            {/* Ticker 30D Price Trend */}
            <Card className="rounded-2xl shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">{cacheDay.ticker} Price Trend (30D)</CardTitle>
                <CardDescription>Rolling window; marker shows current trading day</CardDescription>
              </CardHeader>
              <CardContent className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cacheDay.price30d} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={6} />
                    <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} />
                    <Tooltip
                      formatter={(value: any) => [fmtMoney(Number(value)), "Price"]}
                      labelFormatter={(label: any) => `Date: ${label}`}
                    />
                    <Line type="monotone" dataKey="price" stroke="#2563eb" strokeWidth={2} dot={false} />
                    {/* Marker for current day */}
                    {(() => {
                      const pt = cacheDay.price30d.find((p) => p.date === markedDate);
                      if (!pt) return null;
                      return <ReferenceDot x={pt.date} y={pt.price} r={5} fill="#2563eb" ifOverflow="extendDomain" />;
                    })()}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Market Indices 30D Trend */}
            {(cacheDay.nasdaq30d || cacheDay.dow30d || cacheDay.sp50030d) && (
              <Card className="rounded-2xl shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Market Indices Trend (30D)</CardTitle>
                  <CardDescription>NASDAQ, Dow Jones, S&P 500 normalized (Day 1 = 100)</CardDescription>
                </CardHeader>
                <CardContent className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={(() => {
                        // Combine all three indices data and normalize
                        const combinedData: any[] = [];
                        const dates = new Set<string>();

                        // Collect all unique dates
                        cacheDay.nasdaq30d?.forEach(d => dates.add(d.date));
                        cacheDay.dow30d?.forEach(d => dates.add(d.date));
                        cacheDay.sp50030d?.forEach(d => dates.add(d.date));

                        // Sort dates
                        const sortedDates = Array.from(dates).sort();

                        // Get baseline values (first value for each index)
                        const nasdaqBase = cacheDay.nasdaq30d?.[0]?.price;
                        const dowBase = cacheDay.dow30d?.[0]?.price;
                        const sp500Base = cacheDay.sp50030d?.[0]?.price;

                        // Create combined and normalized data points
                        sortedDates.forEach(date => {
                          const nasdaq = cacheDay.nasdaq30d?.find(d => d.date === date);
                          const dow = cacheDay.dow30d?.find(d => d.date === date);
                          const sp500 = cacheDay.sp50030d?.find(d => d.date === date);

                          combinedData.push({
                            date,
                            nasdaq: nasdaq && nasdaqBase ? (nasdaq.price / nasdaqBase) * 100 : undefined,
                            dow: dow && dowBase ? (dow.price / dowBase) * 100 : undefined,
                            sp500: sp500 && sp500Base ? (sp500.price / sp500Base) * 100 : undefined
                          });
                        });

                        return combinedData;
                      })()}
                      margin={{ top: 10, right: 10, bottom: 0, left: -10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={6} />
                      <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} />
                      <Tooltip
                        formatter={(value: any, name: string) => {
                          const labels: Record<string, string> = {
                            nasdaq: 'NASDAQ',
                            dow: 'Dow Jones',
                            sp500: 'S&P 500'
                          };
                          return [
                            Number(value).toFixed(2),
                            labels[name] || name
                          ];
                        }}
                        labelFormatter={(label: any) => `Date: ${label}`}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: '12px' }}
                        iconType="line"
                      />
                      <Line type="monotone" dataKey="nasdaq" name="NASDAQ" stroke="#16a34a" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="dow" name="Dow Jones" stroke="#dc2626" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="sp500" name="S&P 500" stroke="#f59e0b" strokeWidth={2} dot={false} />
                      {/* Reference line at 100 (baseline) */}
                      <ReferenceLine y={100} stroke="#666" strokeDasharray="3 3" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Portfolio */}
            <Card className="rounded-2xl shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Portfolio</CardTitle>
                <CardDescription>State persists across days (localStorage)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-xl border p-3">
                  <KeyValue k="Ticker" v={cacheDay.ticker} />
                  <KeyValue k="Shares" v={portfolio.shares} />
                  <KeyValue k="Cash" v={fmtMoney(portfolio.cash)} />
                  <KeyValue k="Last price" v={todayPrice ? fmtMoney(todayPrice) : "—"} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border p-3">
                    <div className="text-xs text-muted-foreground">Total value</div>
                    <div className="mt-1 text-lg font-semibold">{fmtMoney(todaysValue)}</div>
                  </div>
                  <div className="rounded-xl border p-3">
                    <div className="text-xs text-muted-foreground">Daily P&L</div>
                    <div className={`mt-1 text-lg font-semibold ${dailyPnl >= 0 ? "text-foreground" : "text-destructive"}`}>
                      {fmtMoney(dailyPnl)}
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border p-3">
                  <div className="text-xs text-muted-foreground">Cumulative return</div>
                  <div className="mt-1 text-lg font-semibold">{(cumReturn * 100).toFixed(2)}%</div>
                </div>
                <SectionHint>
                  Prototype trade sizing: Final step executes a 1-share buy/sell at today&apos;s close. Replace with your own sizing rules.
                </SectionHint>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-6 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              Tip: For multi-day studies, complete d0→d1→d2→d3→d4→Final for each day. Export at the end to collect all logs.
            </div>
            <div>Prototype v0.1 · Local-only storage</div>
          </div>
        </div>
      </div>
    </div>
  );
}
